'use client';

import { m } from 'framer-motion';
import { FileText } from 'lucide-react';
import Link from 'next/link';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="h-8 w-8 text-orange-700" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Use</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Last updated: January 20, 2026
          </p>
        </m.div>

        <m.div
          className="bg-white rounded-2xl shadow-lg p-8 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="prose prose-blue max-w-none">
            <p className="text-gray-700 mb-6">
              Welcome to Axist! These Terms of Use ("Terms") govern your access to and use of our website, services, and applications (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">1. Account Registration</h2>
            <p className="text-gray-700 mb-6">
              To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">2. User Responsibilities</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Violate or encourage others to violate the rights of third parties</li>
              <li>Post, upload, or share content that is unlawful, harmful, or offensive</li>
              <li>Attempt to gain unauthorized access to the Service or related systems</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">3. Intellectual Property</h2>
            <p className="text-gray-700 mb-6">
              The Service and its original content, features, and functionality are and will remain the exclusive property of Axist and its licensors. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">4. User Content</h2>
            <p className="text-gray-700 mb-6">
              You retain ownership of any content you submit, post, or display on or through the Service. By making any content available through the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify, and display such content for the purpose of providing and improving the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">5. Subscription and Billing</h2>
            <p className="text-gray-700 mb-6">
              Some features of the Service may require payment of fees. You agree to pay all applicable fees in connection with your use of the Service. All fees are non-refundable except as required by law. We may change our pricing at any time by posting the changes on the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">6. Termination</h2>
            <p className="text-gray-700 mb-6">
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of these Terms.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">7. Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              In no event shall Axist, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Service.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">8. Governing Law</h2>
            <p className="text-gray-700 mb-6">
              These Terms shall be governed and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">9. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of any changes by posting the updated Terms on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">10. Contact Us</h2>
            <p className="text-gray-700">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-700 mt-2">
              Email: support@axist.app
            </p>
          </div>
        </m.div>

        <div className="text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Axist. All rights reserved. |
            <Link href="/policy" className="text-orange-700 hover:underline ml-1">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}